import { ExamNoticeType } from '@/enums/exam-notice-type.enum';

export type ExamThirdDepthMode = 'fit' | 'overflow';

export interface ExamThirdDepthTab {
  id: string;
  label: string;
}

export interface ExamSecondDepthTab {
  id: string;
  label: string;
  thirdDepthMode?: ExamThirdDepthMode;
  thirdDepthTabs?: readonly ExamThirdDepthTab[];
  defaultThirdDepthId?: string;
}

export interface ExamNoticeMenuConfig {
  type: ExamNoticeType;
  title: string;
  secondDepthTabs?: readonly ExamSecondDepthTab[];
  defaultSecondDepthId?: string;
}

export interface ExamFeatureCard {
  type: ExamNoticeType;
  title: string;
  subtitle?: string;
  pill: string;
  imageSrc: string;
  href: string;
}

export const EXAM_CHECKLIST_TYPE_ORDER = [
  'safety_edu',
  'eligibility',
  'requirements',
  'license_issue',
] as const;

export type ExamChecklistType = (typeof EXAM_CHECKLIST_TYPE_ORDER)[number];

export const EXAM_CHECKLIST_TYPE_LABEL: Record<ExamChecklistType, string> = {
  safety_edu: '교통안전교육',
  eligibility: '시험자격',
  requirements: '준비물',
  license_issue: '면허발급',
};

/**
 * 체크리스트 메타 description — `type`(2차 탭이 있으면 `sub`)별로 고유 문장.
 *
 * 옛 구조는 8개 URL이 한 문장을 공유했다("교통안전교육, 시험자격, 준비물,
 * 면허발급까지 …"). 빙 웹마스터도구가 `Too many pages with identical meta
 * descriptions`로 잡았고(2026-08-13), 실측하면 3건이 아니라 **8건 전부**가
 * 글자 하나까지 같았다. 탭마다 본문이 다른데 스니펫만 같아 검색엔진이 어느
 * 탭을 띄울지 고를 근거가 없었다.
 *
 * 시험안내(`EXAM_NOTICE_DESCRIPTION_SUFFIX`)는 이미 탭별로 갈라 쓰고 있어,
 * 같은 방식을 체크리스트에도 맞춘다. 문장은 각 탭이 **실제로 담고 있는 항목**
 * 에서 뽑는다(교통안전교육=교육대상·시기·시간, 신체검사=장소·준비물·수수료,
 * 면허발급=발급대상·장소·구비서류 …). 근거 없는 수식은 넣지 않는다.
 */
export const EXAM_CHECKLIST_DESCRIPTION: Record<string, string> = {
  'safety_edu:traffic_safety':
    '운전면허 학과시험 전에 받아야 하는 교통안전교육의 교육대상과 교육시기, 교육시간, 준비물과 수강신청 절차를 정리했습니다. 도로교통공단 자료를 기준으로 운전면허 플러스에서 확인하세요.',
  'safety_edu:physical_exam':
    '운전면허 신체검사를 받을 수 있는 장소와 준비물, 수수료를 정리했습니다. 시험장 신체검사실과 병원 중 어디서 받을지 정할 때 운전면허 플러스에서 확인하세요.',
  'eligibility:exam_exempt':
    '운전면허 시험면제 대상을 면허 종류별 표로 정리했습니다. 이미 가진 면허에 따라 학과시험·기능시험 중 무엇이 면제되는지 운전면허 플러스에서 확인하세요.',
  'eligibility:disqualification':
    '운전면허 결격사유를 연령·경력, 정신질환 등, 신체조건으로 나눠 정리했습니다. 응시 전에 본인이 해당되는 항목이 있는지 운전면허 플러스에서 확인하세요.',
  'eligibility:exam_restriction':
    '음주운전·무면허 등으로 면허가 취소된 경우의 응시제한 기간을 5년·4년·3년·2년·1년·6개월과 즉시 응시 가능한 경우로 나눠 정리했습니다. 운전면허 플러스에서 확인하세요.',
  'eligibility:qualification':
    '운전면허 종류별 응시 자격과 신체검사 기준을 정리했습니다. 시력과 교정시력, 색채식별, 청력, 신체 조건 기준을 운전면허 플러스에서 확인하세요.',
  requirements:
    '운전면허 시험 응시에 필요한 준비물을 단계별로 정리했습니다. 응시원서와 신분증, 사진, 수수료 등 시험장에 가기 전 챙길 것을 운전면허 플러스에서 확인하세요.',
  license_issue:
    '운전면허증 발급 대상과 발급 장소, 구비서류와 수수료를 정리했습니다. 최종 합격 후 운전면허증(국문·영문)과 모바일 운전면허증 발급 절차를 운전면허 플러스에서 확인하세요.',
};

/** 체크리스트 탭 조합 → description. 2차 탭이 없는 type은 type만으로 찾는다. */
export function getExamChecklistDescription(resolved: {
  type: ExamChecklistType;
  secondDepth: string;
}): string {
  return (
    EXAM_CHECKLIST_DESCRIPTION[`${resolved.type}:${resolved.secondDepth}`] ??
    EXAM_CHECKLIST_DESCRIPTION[resolved.type] ??
    EXAM_CHECKLIST_DESCRIPTION.requirements
  );
}

export interface ExamChecklistMenuConfig {
  type: ExamChecklistType;
  title: string;
  secondDepthTabs?: readonly ExamSecondDepthTab[];
  defaultSecondDepthId?: string;
}

export const EXAM_CHECKLIST_MENU: Record<
  ExamChecklistType,
  ExamChecklistMenuConfig
> = {
  safety_edu: {
    type: 'safety_edu',
    title: EXAM_CHECKLIST_TYPE_LABEL.safety_edu,
    defaultSecondDepthId: 'traffic_safety',
    secondDepthTabs: [
      { id: 'traffic_safety', label: '교통안전교육' },
      { id: 'physical_exam', label: '신체검사' },
    ],
  },
  eligibility: {
    type: 'eligibility',
    title: EXAM_CHECKLIST_TYPE_LABEL.eligibility,
    defaultSecondDepthId: 'exam_exempt',
    secondDepthTabs: [
      { id: 'exam_exempt', label: '시험면제' },
      { id: 'disqualification', label: '운전면허 결격사유' },
      { id: 'exam_restriction', label: '응시제한' },
      { id: 'qualification', label: '시험자격' },
    ],
  },
  requirements: {
    type: 'requirements',
    title: EXAM_CHECKLIST_TYPE_LABEL.requirements,
  },
  license_issue: {
    type: 'license_issue',
    title: EXAM_CHECKLIST_TYPE_LABEL.license_issue,
  },
};

export interface ExamChecklistItem {
  label: string;
  iconSrc: string;
  checklistType: ExamChecklistType;
}

export const EXAM_NOTICE_TYPE_LABEL: Record<ExamNoticeType, string> = {
  [ExamNoticeType.PROCESS]: '면허취득절차',
  [ExamNoticeType.WRITTEN]: '학과시험',
  [ExamNoticeType.SKILL]: '장내기능시험',
  [ExamNoticeType.ROAD]: '도로주행시험',
};

export const EXAM_NOTICE_TYPE_ORDER: readonly ExamNoticeType[] = [
  ExamNoticeType.PROCESS,
  ExamNoticeType.WRITTEN,
  ExamNoticeType.SKILL,
  ExamNoticeType.ROAD,
] as const;

/** 시험안내 메타 description의 type별 마무리 문장 (탭 라벨과 결합해 고유화) */
export const EXAM_NOTICE_DESCRIPTION_SUFFIX: Record<ExamNoticeType, string> = {
  [ExamNoticeType.PROCESS]:
    '운전면허 취득 절차와 단계별 준비 사항을 운전면허플러스에서 확인하세요.',
  [ExamNoticeType.WRITTEN]:
    '학과(필기)시험 안내와 면허종별 차이, 학습 활용법을 운전면허플러스에서 확인하세요.',
  [ExamNoticeType.SKILL]:
    '장내기능(실기)시험 코스와 채점 항목, 교육기관 정보를 운전면허플러스에서 확인하세요.',
  [ExamNoticeType.ROAD]:
    '도로주행(실기)시험 안내와 교육기관 정보를 운전면허플러스에서 확인하세요.',
};

/**
 * 시험안내 탭 조합(`type:sub:step`) → description.
 *
 * 종전에는 `EXAM_NOTICE_DESCRIPTION_SUFFIX` 하나로 **type 4개**만 갈랐다. 그래서
 * 20개 탭이 4개 문장을 나눠 쓰는 꼴이었고, 길이도 56~82자로 짧았다(빙 웹마스터도구
 * `Meta descriptions ... too short` 지목, 실측 2026-08-18). 스니펫으로서 정보가 없고,
 * 같은 문장을 공유하는 URL 이 최대 8개라 근사중복 신호도 됐다.
 *
 * 문장은 각 탭이 **실제로 담고 있는 항목**에서 뽑는다(합격기준·수수료·코스 구성·
 * 실격기준 …). 체크리스트(`EXAM_CHECKLIST_DESCRIPTION`)와 같은 규칙이다 —
 * 근거 없는 수식은 넣지 않는다.
 */
export const EXAM_NOTICE_DESCRIPTION: Record<string, string> = {
  process:
    '운전면허 취득 절차를 교통안전교육·신체검사·학과 접수·학과시험·기능 접수·기능시험·연습면허 발급·도로주행 접수·도로주행 시험·면허증 발급 10단계로 정리했습니다. 단계별 준비물과 재응시 조건을 운전면허 플러스에서 확인하세요.',
  'written:notice':
    '운전면허 학과시험의 합격기준(1종 70점·2종 60점)과 시험시간 40분, 문장형·사진형·일러스트형·안전표지형·동영상형으로 이뤄진 40문제 구성, 준비물과 응시 가능 언어를 정리했습니다. 운전면허 플러스에서 확인하세요.',
  'written:license_type':
    '운전면허 학과시험을 2종 보통·1종 보통·1종 대형·1종 특수·2종 소형·2종 원동기 등 면허종별로 나눠 합격기준과 응시 자격 연령, 수수료를 비교했습니다. 어떤 면허를 볼지 정할 때 운전면허 플러스에서 확인하세요.',
  'written:usage':
    '운전면허 학과시험을 운전면허 플러스 앱으로 준비하는 방법입니다. 실제 시험처럼 시간을 재며 푸는 모의고사 40제, 문장형·사진형·일러스트형 등 유형별 학습, 시험 직전에 틀린 문제만 모아 보는 급할 때 200문제를 안내합니다.',
  'skill:notice:normal':
    '1·2종 보통 장내기능시험의 합격기준 80점과 수수료 25,000원, 준비물을 정리했습니다. 주행거리 300m 이상 코스에서 운전장치 조작, 차로 준수·급정지, 경사로, 좌·우회전, 직각주차, 신호교차로, 가속구간을 평가합니다.',
  'skill:notice:large':
    '1종 대형 장내기능시험의 합격기준 80점과 수수료 25,000원, 준비물과 실격기준을 정리했습니다. 출발·굴절·곡선·방향전환·평행주차·기어변속·신호교차로·횡단보도·철길건널목·경사로·종료 코스를 차례로 안내합니다.',
  'skill:notice:special':
    '1종 특수 장내기능시험의 합격기준 90점과 수수료 25,000원, 준비물과 실격기준을 정리했습니다. 대형 견인차는 연결·코스·분리, 소형 견인차와 구난차는 굴절·곡선·방향전환 코스로 나뉩니다.',
  'skill:notice:small':
    '2종 소형(125cc 초과 이륜자동차) 장내기능시험의 합격기준 90점과 수수료 14,000원, 준비물을 정리했습니다. 굴절·곡선·좁은길·연속진로전환 코스와 출발 지연·과제 미이행 등 실격기준을 함께 확인하세요.',
  'skill:notice:motor':
    '2종 원동기장치자전거 장내기능시험의 합격기준 90점과 수수료 10,000원, 준비물을 정리했습니다. 굴절·곡선·좁은길·연속진로전환 코스(다륜형은 굴절·곡선만)와 실격기준을 함께 확인하세요.',
  'skill:agency':
    '장내기능시험을 준비할 수 있는 교육기관을 비교했습니다. 학원에서 시험까지 치르는 전문학원과 시험장에서 응시하는 일반학원의 차이, 시뮬레이터로 코스를 반복 연습하는 실내운전연습장을 운전면허 플러스에서 확인하세요.',
  'skill:course:before':
    '장내기능시험 첫 단계인 출발 전 준비입니다. 승차 후 운전석과 후사경 조정, 안전벨트 착용 등 점검 항목과 주의사항을 정리했습니다. 코스 순서는 전국 운전면허시험장·전문학원이 동일합니다.',
  'skill:course:start':
    '장내기능시험 장치 조작 및 출발 단계입니다. 시동 걸기와 기어변속기·전조등·방향지시등·와이퍼 조작 평가, 안내음성에 따른 출발 절차와 주의사항을 정리했습니다.',
  'skill:course:slope':
    '장내기능시험 경사로 코스입니다. 출발 후 가장 먼저 진행하는 구간으로, 정해진 위치에 정지하는 방법과 정지 편차 기준, 재출발 요령과 주의사항을 정리했습니다.',
  'skill:course:intersection':
    '장내기능시험 교차로 코스입니다. 시험 중 두 번 진행되며, 신호에 따른 직진과 좌회전 주행방법, 정지선 준수와 방향지시등 사용 등 평가 요소를 정리했습니다.',
  'skill:course:parking':
    '장내기능시험 직각주차 코스입니다. 코스 진입 위치 잡기와 핸들 조작, 후진 속도 조절, 주차 후 다시 출발하기까지 단계별 방법과 주의사항을 정리했습니다.',
  'skill:course:curve':
    '장내기능시험 가속구간입니다. 20km/h 이상으로 올리는 가속 요령과 속도 유지 시간, 이후 감속까지 속도 조절과 페달 조작의 안정성을 어떻게 평가하는지 정리했습니다.',
  'skill:course:emergency':
    '장내기능시험 돌발상황입니다. 안내음성 후 2초 이내 정지, 3초 이내 비상등 점등 등 대응 절차와 상황 종료 후 출발 방법, 감점 요인을 정리했습니다.',
  'skill:course:finish':
    '장내기능시험 종료 단계입니다. 백색선 통과 시 우측 방향지시등 점등, 정차 후 기어 P·주차 브레이크 체결·시동 끄기까지 마무리 절차와 감점 요인을 정리했습니다.',
  'road:notice':
    '도로주행시험의 합격기준 70점과 수수료 30,000원, 준비물을 정리했습니다. 총 연장 5km 이상 4개 코스 중 추첨으로 1개를 배정받아 내비게이션 음성 안내로 주행하며, 안전운전 57개 항목과 실격기준을 확인하세요.',
  'road:agency':
    '도로주행시험을 준비할 수 있는 교육기관을 비교했습니다. 학원에서 시험까지 치르는 전문학원과 시험장에서 응시해야 하는 일반학원의 차이, 실내운전연습장 활용법을 운전면허 플러스에서 확인하세요.',
};

/**
 * 시험안내 탭 조합 → description. 좁은 키(`type:sub:step`)부터 넓은 키(`type`) 순으로
 * 찾고, 아무것도 없으면 종전 type 단위 문장으로 떨어진다(탭이 새로 생겨도 빈 값이
 * 나가지 않게 하는 안전망).
 */
export function getExamNoticeDescription(resolved: {
  type: ExamNoticeType;
  secondDepth: string;
  thirdDepth: string;
}): string {
  const { type, secondDepth, thirdDepth } = resolved;
  return (
    EXAM_NOTICE_DESCRIPTION[`${type}:${secondDepth}:${thirdDepth}`] ??
    EXAM_NOTICE_DESCRIPTION[`${type}:${secondDepth}`] ??
    EXAM_NOTICE_DESCRIPTION[type] ??
    `운전면허 ${EXAM_NOTICE_DESCRIPTION_SUFFIX[type]}`
  );
}

export const EXAM_NOTICE_MENU: Record<ExamNoticeType, ExamNoticeMenuConfig> = {
  [ExamNoticeType.PROCESS]: {
    type: ExamNoticeType.PROCESS,
    title: EXAM_NOTICE_TYPE_LABEL[ExamNoticeType.PROCESS],
    defaultSecondDepthId: 'notice',
  },
  [ExamNoticeType.WRITTEN]: {
    type: ExamNoticeType.WRITTEN,
    title: EXAM_NOTICE_TYPE_LABEL[ExamNoticeType.WRITTEN],
    defaultSecondDepthId: 'notice',
    secondDepthTabs: [
      { id: 'notice', label: '시험 안내' },
      { id: 'license_type', label: '면허종별 차이점' },
      { id: 'usage', label: '운플 활용법' },
    ],
  },
  [ExamNoticeType.SKILL]: {
    type: ExamNoticeType.SKILL,
    title: EXAM_NOTICE_TYPE_LABEL[ExamNoticeType.SKILL],
    defaultSecondDepthId: 'notice',
    secondDepthTabs: [
      {
        id: 'notice',
        label: '시험 안내',
        thirdDepthMode: 'overflow',
        defaultThirdDepthId: 'normal',
        thirdDepthTabs: [
          { id: 'normal', label: '1ㆍ2종 보통' },
          { id: 'large', label: '1종 대형' },
          { id: 'special', label: '1종 특수' },
          { id: 'small', label: '2종 소형' },
          { id: 'motor', label: '2종 원동기' },
        ],
      },
      {
        id: 'course',
        label: '시험 코스 및 항목',
        thirdDepthMode: 'fit',
        defaultThirdDepthId: 'before',
        thirdDepthTabs: [
          { id: 'before', label: '출발 전 준비' },
          { id: 'start', label: '장치조작 및 출발' },
          { id: 'slope', label: '경사로' },
          { id: 'intersection', label: '교차로' },
          { id: 'parking', label: '직각주차' },
          { id: 'curve', label: '가속구간' },
          { id: 'emergency', label: '돌발상황' },
          { id: 'finish', label: '종료방법' },
        ],
      },
      { id: 'agency', label: '교육기관' },
    ],
  },
  [ExamNoticeType.ROAD]: {
    type: ExamNoticeType.ROAD,
    title: EXAM_NOTICE_TYPE_LABEL[ExamNoticeType.ROAD],
    defaultSecondDepthId: 'notice',
    secondDepthTabs: [
      {
        id: 'notice',
        label: '시험 안내',
      },
      { id: 'agency', label: '교육기관' },
    ],
  },
};

export const EXAM_FEATURE_CARDS: readonly ExamFeatureCard[] = [
  {
    type: ExamNoticeType.PROCESS,
    title: EXAM_NOTICE_TYPE_LABEL[ExamNoticeType.PROCESS],
    pill: '한눈에 보는 로드맵',
    imageSrc: '/images/exam/exam_menu_process.webp',
    href: buildExamNoticeHref(ExamNoticeType.PROCESS),
  },
  {
    type: ExamNoticeType.WRITTEN,
    title: EXAM_NOTICE_TYPE_LABEL[ExamNoticeType.WRITTEN],
    subtitle: '필기',
    pill: '필기 시험 백점비법',
    imageSrc: '/images/exam/exam_menu_written.webp',
    href: buildExamNoticeHref(ExamNoticeType.WRITTEN),
  },
  {
    type: ExamNoticeType.SKILL,
    title: EXAM_NOTICE_TYPE_LABEL[ExamNoticeType.SKILL],
    pill: '시험절차 및 코스안내',
    imageSrc: '/images/exam/exam_menu_skill.webp',
    href: buildExamNoticeHref(ExamNoticeType.SKILL),
  },
  {
    type: ExamNoticeType.ROAD,
    title: EXAM_NOTICE_TYPE_LABEL[ExamNoticeType.ROAD],
    pill: '도로주행 준비 가이드',
    imageSrc: '/images/exam/exam_menu_road.webp',
    href: buildExamNoticeHref(ExamNoticeType.ROAD),
  },
] as const;

export const EXAM_CHECKLIST_ITEMS: readonly ExamChecklistItem[] = [
  {
    label: '교통안전교육',
    iconSrc: '/images/exam/icon_safety_edu.svg',
    checklistType: 'safety_edu',
  },
  {
    label: '시험자격',
    iconSrc: '/images/exam/icon_eligibility.svg',
    checklistType: 'eligibility',
  },
  {
    label: '준비물',
    iconSrc: '/images/exam/icon_requirements.svg',
    checklistType: 'requirements',
  },
  {
    label: '면허발급',
    iconSrc: '/images/exam/icon_get_license.svg',
    checklistType: 'license_issue',
  },
] as const;

export function getExamNoticeType(type: string | undefined): ExamNoticeType {
  if (!type) return ExamNoticeType.PROCESS;
  if (EXAM_NOTICE_TYPE_ORDER.includes(type as ExamNoticeType)) {
    return type as ExamNoticeType;
  }
  return ExamNoticeType.PROCESS;
}

/** 시험안내 URL의 `sub`(2차 탭)·`step`(3차 탭)을 메뉴 구조에 맞게 해석 */
export function resolveExamNoticeSelection(
  typeParam: string | undefined,
  subParam: string | undefined,
  stepParam: string | undefined,
): {
  type: ExamNoticeType;
  secondDepth: string;
  thirdDepth: string;
} {
  const type = getExamNoticeType(typeParam);
  const menu = EXAM_NOTICE_MENU[type];
  const tabs = menu.secondDepthTabs ?? [];

  const secondDepth =
    subParam && tabs.some((t) => t.id === subParam)
      ? subParam
      : (menu.defaultSecondDepthId ?? tabs[0]?.id ?? '');

  const secondConfig =
    tabs.find((t) => t.id === secondDepth) ?? tabs[0] ?? undefined;

  let thirdDepth = '';
  const thirds = secondConfig?.thirdDepthTabs;
  if (thirds?.length && secondConfig) {
    thirdDepth =
      stepParam && thirds.some((t) => t.id === stepParam)
        ? stepParam
        : (secondConfig.defaultThirdDepthId ?? thirds[0]?.id ?? '');
  }

  return { type, secondDepth, thirdDepth };
}

/** 해석된 시험안내 선택값을 메뉴 라벨로 변환 (메타데이터·브레드크럼용) */
export function getExamNoticeLabels(resolved: {
  type: ExamNoticeType;
  secondDepth: string;
  thirdDepth: string;
}): { typeLabel: string; secondLabel: string; thirdLabel: string } {
  const typeLabel = EXAM_NOTICE_TYPE_LABEL[resolved.type];
  const tabs = EXAM_NOTICE_MENU[resolved.type].secondDepthTabs ?? [];
  const secondConfig = tabs.find((t) => t.id === resolved.secondDepth);
  const secondLabel = secondConfig?.label ?? '';
  const thirdLabel =
    secondConfig?.thirdDepthTabs?.find((t) => t.id === resolved.thirdDepth)
      ?.label ?? '';
  return { typeLabel, secondLabel, thirdLabel };
}

/** 쿼리스트링을 붙인 시험안내 경로 (`type` 필수, 2·3차는 탭이 있을 때만) */
export function buildExamNoticeUrl(resolved: {
  type: ExamNoticeType;
  secondDepth: string;
  thirdDepth: string;
}): string {
  const params = new URLSearchParams();
  params.set('type', resolved.type);
  const menu = EXAM_NOTICE_MENU[resolved.type];
  const tabs = menu.secondDepthTabs ?? [];
  if (tabs.length > 0 && resolved.secondDepth) {
    params.set('sub', resolved.secondDepth);
  }
  const secondConfig = tabs.find((t) => t.id === resolved.secondDepth);
  if (secondConfig?.thirdDepthTabs?.length && resolved.thirdDepth) {
    params.set('step', resolved.thirdDepth);
  }
  return `/exam/notice?${params.toString()}`;
}

export function buildExamNoticeHref(type: ExamNoticeType) {
  const resolved = resolveExamNoticeSelection(type, undefined, undefined);
  return buildExamNoticeUrl(resolved);
}

export function getExamChecklistType(
  typeParam: string | undefined,
): ExamChecklistType {
  if (
    typeParam &&
    EXAM_CHECKLIST_TYPE_ORDER.includes(typeParam as ExamChecklistType)
  ) {
    return typeParam as ExamChecklistType;
  }
  return EXAM_CHECKLIST_TYPE_ORDER[0];
}

/** `/exam/checklist`의 `type`·`sub`를 메뉴 구조에 맞게 해석(시험안내와 동일한 쿼리 키) */
export function resolveExamChecklistSelection(
  typeParam: string | undefined,
  subParam: string | undefined,
): {
  type: ExamChecklistType;
  secondDepth: string;
} {
  const type = getExamChecklistType(typeParam);
  const menu = EXAM_CHECKLIST_MENU[type];
  const tabs = menu.secondDepthTabs ?? [];

  const secondDepth =
    subParam && tabs.some((t) => t.id === subParam)
      ? subParam
      : tabs.length > 0
        ? (menu.defaultSecondDepthId ?? tabs[0]?.id ?? '')
        : (menu.defaultSecondDepthId ?? '');

  return { type, secondDepth };
}

export function buildExamChecklistUrl(resolved: {
  type: ExamChecklistType;
  secondDepth: string;
}): string {
  const params = new URLSearchParams();
  params.set('type', resolved.type);
  const menu = EXAM_CHECKLIST_MENU[resolved.type];
  const tabs = menu.secondDepthTabs ?? [];
  if (tabs.length > 0 && resolved.secondDepth) {
    params.set('sub', resolved.secondDepth);
  }
  return `/exam/checklist?${params.toString()}`;
}

export function buildExamChecklistHref(
  checklistType: ExamChecklistType,
  sub?: string,
) {
  const resolved = resolveExamChecklistSelection(
    checklistType,
    sub ?? undefined,
  );
  return buildExamChecklistUrl(resolved);
}

/** 앱 웹뷰·회원 컨텍스트 전달용 — 시험안내 내 라우팅 시 유지 */
export const EXAM_WEBVIEW_BRIDGE_PARAM_KEYS = [
  'hash',
  'isApp',
  'license',
  'encryptLocation',
  'location',
  'encryptLatitude',
  'latitude',
  'encryptLongitude',
  'longitude',
] as const;

/** `href`에 아직 없는 브리지 파라미터만 `source`(현재 URL 등)에서 보강 */
export function appendExamAppBridgeQuery(
  href: string,
  source: { get: (name: string) => string | null },
): string {
  const qIndex = href.indexOf('?');
  const path = qIndex >= 0 ? href.slice(0, qIndex) : href;
  const params = new URLSearchParams(qIndex >= 0 ? href.slice(qIndex + 1) : '');
  for (const key of EXAM_WEBVIEW_BRIDGE_PARAM_KEYS) {
    if (params.has(key)) continue;
    const v = source.get(key);
    if (v != null && v !== '') params.set(key, v);
  }
  const s = params.toString();
  return s ? `${path}?${s}` : path;
}
